import React from 'react';
import {
  Action, FormHandlers, HStack, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Offer } from '../../../providers/api/dtos';
import { offerAcceptance } from '../../../providers/api/offer/offerProvider';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import CounterOfferForm, { CounterOfferFormModel } from '../../components/offer-wizard/counter-offer-form/CounterOfferForm';
import { counterOfferFormModelToCounterOffer, counterOfferToCounterOfferFormModel } from '../../components/offer-wizard/counter-offer-form/mappers';
import useOfferAcceptance from '../../hooks/use-offer-acceptance/useOfferAcceptance';
import { useComputedOfferStatus } from '../../hooks/use-computed-offer-status/useComputedOfferStatus';
import { DigitalOfferStatus, DocumentStatus, OfferDocumentTypeValue } from '../../../domain/types';
import { useOfferDocuments } from '../../hooks/use-offer-documents/useOfferDocuments';

export const PROPOSE_COUNTER_OFFER_SUCCESS_MESSAGE = 'La richiesta di firma digitale della contro proposta è stata inviata con successo.';
export const PROPOSE_COUNTER_OFFER_ERROR_MESSAGE = 'Non è stato possibile inviare la richiesta di firma digitale della contro proposta.';

interface ChildFnProps {
  proposeCounterOffer: VoidFunction,
  isLoading: boolean,
}

export interface ProposeCounterOfferActionProps extends React.AriaAttributes {
  offer: Offer,
  onSuccess?: VoidFunction,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

const ProposeCounterOfferAction: React.FC<ProposeCounterOfferActionProps> = ({
  offer,
  onSuccess,
  children,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const { data: acceptance } = useOfferAcceptance(offer.id);
  const { data: computedOfferStatus } = useComputedOfferStatus(offer.intentId, offer);
  const { data: offerDocuments } = useOfferDocuments(offer?.id);

  const hasPurchaseProposalApproved = React.useMemo(() => offerDocuments?.some(
    (doc) => doc.type === OfferDocumentTypeValue.PURCHASE_PROPOSAL && doc.status === DocumentStatus.APPROVED,
  ), [offerDocuments]);

  const handleSubmit = React.useCallback(async (formValues: CounterOfferFormModel) => {
    try {
      setIsLoading(true);

      const counterOffer = counterOfferFormModelToCounterOffer(formValues);

      await offerAcceptance(offer.id!, { counterOffer });

      setIsLoading(false);
      modal.close();

      addSuccess(PROPOSE_COUNTER_OFFER_SUCCESS_MESSAGE);
      onSuccess?.();
    } catch (_) {
      setIsLoading(false);
      addError(PROPOSE_COUNTER_OFFER_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, modal, offer.id, onSuccess]);

  if (hasPurchaseProposalApproved || !computedOfferStatus?.canBeUpdatedTo.includes(DigitalOfferStatus.WAITING_FOR_COUNTER_OFFER_ACCEPTANCE)) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({ proposeCounterOffer: modal.open, isLoading })
          : (
            <Action
              aria-label="Invia la richiesta di controproposta"
              label="Invia controproposta"
              onClick={() => modal.open()}
              size="S"
              loading={isLoading}
            />
          )
      }
      <Portal>
        <SimpleModal
          {...modal}
          title="Invia controproposta"
          aria-label="Modale per inviare la richiesta di controproposta"
          footer={(
            <HStack justifyContent="end">
              <Action
                label="Invia"
                color="primary"
                emphasis="high"
                loading={isLoading}
                aria-label="Pulsante per inviare la richiesta di controproposta"
                onClick={() => formRef.current.handleSubmit()}
              />
              <Action
                label="Annulla"
                color="neutral"
                emphasis="high"
                loading={isLoading}
                aria-label="Pulsante per annullare la richiesta di controproposta"
                onClick={modal.close}
              />
            </HStack>
          )}
        >
          <CounterOfferForm
            innerRef={formRef}
            offerValue={offer.value}
            initialValues={counterOfferToCounterOfferFormModel(acceptance?.counterOffer)}
            onSubmit={handleSubmit}
            loading={isLoading}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default ProposeCounterOfferAction;
